import { AxiosResponse } from 'axios';
import { mapProfileToPerson } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import { asResponse, paginationRange, buildPaginatedResponse } from '@/lib/supabase-helpers';
import { PersonType, Team, TeamArea, TeamCategory, TeamsApiResponse } from '@/types/api';

async function buildTeam(team: any): Promise<Team> {
  // Fetch active members (no end_date = still on the team)
  const { data: teamMembers } = await supabase
    .from('team_members')
    .select(
      'profile_id, role, profiles (id, first_name, last_name, email, phone, picture_url, developer_type)'
    )
    .eq('team_id', team.id)
    .is('end_date', null);

  const members = (teamMembers || []).map((tm: any) => ({
    id: tm.profile_id,
    teamId: team.id,
    user: null,
    userId: tm.profile_id,
    person: tm.profiles ? mapProfileToPerson(tm.profiles) : null,
    role: tm.role,
    createdAt: tm.created_at ?? '',
    updatedAt: tm.updated_at ?? '',
  }));

  // Fetch manager
  let manager: PersonType | null = null;
  if (team.manager_id) {
    const { data: managerProfile } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email, phone, picture_url, developer_type')
      .eq('id', team.manager_id)
      .single();
    if (managerProfile) {
      manager = mapProfileToPerson(managerProfile);
    }
  }

  return {
    id: team.id,
    name: team.name,
    area: team.area,
    category: team.category,
    managerId: team.manager_id,
    manager,
    pictureUrl: team.picture_url,
    ptoPolicyId: null,
    members,
    createdAt: team.created_at,
    updatedAt: team.updated_at,
  } as Team;
}

export type CreateATeamParams = {
  area?: TeamArea;
  category?: TeamCategory;
  managerId?: string;
  personIdList?: string[];
  name: string;
  file?: File;
};

export type TeamMembersParams = {
  teamId: string;
  userId: string;
};

const getAllTeams = async ({
  page,
  take,
  term,
}: {
  page: number;
  take: number;
  term?: string;
}): Promise<AxiosResponse<TeamsApiResponse>> => {
  const [from, to] = paginationRange(page, take);

  let query = supabase.from('teams').select('*', { count: 'exact' }).range(from, to).order('name');

  if (term) {
    query = query.ilike('name', `%${term}%`);
  }

  const { data: teams, count, error } = await query;
  if (error) throw error;

  const builtTeams = await Promise.all((teams || []).map(buildTeam));

  const totalCount = count || 0;

  return asResponse({
    teams: buildPaginatedResponse(builtTeams, totalCount, page, take),
  } as TeamsApiResponse);
};

export type CreateTeamResponse = Pick<
  Team,
  'createdAt' | 'id' | 'managerId' | 'name' | 'pictureUrl' | 'ptoPolicyId' | 'updatedAt'
>;

const createATeam = async ({
  area,
  category,
  managerId,
  personIdList = [],
  name,
}: CreateATeamParams): Promise<AxiosResponse<CreateTeamResponse>> => {
  const { data: team, error } = await supabase
    .from('teams')
    .insert({
      name,
      area: area || null,
      category: category || null,
      manager_id: managerId || null,
    })
    .select()
    .single();

  if (error) throw error;

  // Add members
  if (personIdList.length) {
    await supabase.from('team_members').insert(
      personIdList.map((profileId) => ({
        team_id: team.id,
        profile_id: profileId,
      }))
    );
  }

  return asResponse({
    id: team.id,
    name: team.name,
    managerId: team.manager_id,
    pictureUrl: team.picture_url,
    ptoPolicyId: null,
    createdAt: team.created_at,
    updatedAt: team.updated_at,
  } as CreateTeamResponse);
};

export type EditTeamParams = Partial<CreateATeamParams> & {
  id: string;
};

const editTeam = async ({
  id,
  area,
  category,
  managerId,
  personIdList,
  name,
}: EditTeamParams): Promise<AxiosResponse<Team>> => {
  const updates: any = {};
  if (name) updates.name = name;
  if (area) updates.area = area;
  if (category) updates.category = category;
  if (managerId) updates.manager_id = managerId;

  if (Object.keys(updates).length) {
    await supabase.from('teams').update(updates).eq('id', id);
  }

  // Update members: diff old vs new instead of delete-all + re-insert.
  // This preserves end_date history for departed members.
  if (personIdList) {
    const { data: currentMembers } = await supabase
      .from('team_members')
      .select('profile_id')
      .eq('team_id', id)
      .is('end_date', null);

    const currentIds = new Set((currentMembers ?? []).map((m: any) => m.profile_id));
    const newIds = new Set(personIdList);

    // Soft-remove members no longer in the list (set end_date)
    const removedIds = [...currentIds].filter((pid) => !newIds.has(pid));
    if (removedIds.length > 0) {
      await supabase
        .from('team_members')
        .update({ end_date: new Date().toISOString().split('T')[0] })
        .eq('team_id', id)
        .in('profile_id', removedIds)
        .is('end_date', null);
    }

    // Add new members not currently active
    const addedIds = [...newIds].filter((pid) => !currentIds.has(pid));
    if (addedIds.length > 0) {
      await supabase.from('team_members').insert(
        addedIds.map((profileId) => ({
          team_id: id,
          profile_id: profileId,
        }))
      );
    }
  }

  const { data: team } = await supabase.from('teams').select('*').eq('id', id).single();
  return asResponse(await buildTeam(team));
};

const deleteTeam = async (teamId: string): Promise<AxiosResponse<string>> => {
  const { error } = await supabase.from('teams').delete().eq('id', teamId);
  if (error) throw error;
  return asResponse('ok');
};

const getTeamMembers = async (teamId: string): Promise<AxiosResponse<Team>> => {
  const { data: team } = await supabase.from('teams').select('*').eq('id', teamId).single();
  if (!team) throw new Error('Team not found');
  return asResponse(await buildTeam(team));
};

const addMemberToTheTeam = async ({
  teamId,
  userId,
}: TeamMembersParams): Promise<AxiosResponse<string>> => {
  const { error } = await supabase
    .from('team_members')
    .insert({ team_id: teamId, profile_id: userId });
  if (error) throw error;
  return asResponse('ok');
};

const removeMemberFromTheTeam = async ({
  teamId,
  userId,
}: TeamMembersParams): Promise<AxiosResponse<string>> => {
  // Soft-delete: set end_date instead of deleting the row so history is preserved
  // for FinanceX profitability and PeopleX records.
  const { error } = await supabase
    .from('team_members')
    .update({ end_date: new Date().toISOString().split('T')[0] })
    .eq('team_id', teamId)
    .eq('profile_id', userId)
    .is('end_date', null);
  if (error) throw error;
  return asResponse('ok');
};

const setMemberEndDate = async (
  teamId: string,
  profileId: string,
  endDate: string
): Promise<AxiosResponse<string>> => {
  const { error } = await supabase
    .from('team_members')
    .update({ end_date: endDate })
    .eq('team_id', teamId)
    .eq('profile_id', profileId)
    .is('end_date', null);
  if (error) throw error;
  return asResponse('ok');
};

const getTeam = async (teamId: string): Promise<AxiosResponse<Team>> => {
  const { data: team } = await supabase.from('teams').select('*').eq('id', teamId).single();
  if (!team) throw new Error('Team not found');
  return asResponse(await buildTeam(team));
};

export default {
  getAllTeams,
  removeMemberFromTheTeam,
  setMemberEndDate,
  addMemberToTheTeam,
  createATeam,
  getTeamMembers,
  deleteTeam,
  editTeam,
  getTeam,
};
